import React, { useEffect, useState, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Coordinate } from '../../../../dataTypes/geolocationManagment/types';
import styles from './OpenStreetMap.module.scss';

export const TILE_URLS = {
  street:        'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  darkLabels:    'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
  darkNoLabels:  'https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png',
  lightLabels:   'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
  lightNoLabels: 'https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png',
  voyager:       'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
} as const;

interface OpenStreetMapProps {
  coordinates: Coordinate[];
  width?: string;
  height?: string;
  initialZoom?: number;
  renderMode?: 'street' | 'dark';
  tileUrl?: string;
}

/* =========================
   Helper hex → "r,g,b"
========================= */
function hexToRgb(hex: string): string {
  const sanitized = hex.replace('#', '');
  const r = parseInt(sanitized.slice(0, 2), 16);
  const g = parseInt(sanitized.slice(2, 4), 16);
  const b = parseInt(sanitized.slice(4, 6), 16);
  return `${r},${g},${b}`;
}

/* =========================
   Custom Zoom Control
========================= */
const CustomZoomControl = () => {
  const map = useMap();
  return (
    <div className={styles.customZoom}>
      <button onClick={() => map.zoomIn()}>+</button>
      <button onClick={() => map.zoomOut()}>−</button>
    </div>
  );
};

/* =========================
   Heatmap Canvas (mode dark)
========================= */
const HeatmapCanvas = ({ coordinates }: { coordinates: Coordinate[] }) => {
  const map = useMap();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const size = map.getSize();
    canvas.width = size.x;
    canvas.height = size.y;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, size.x, size.y);

    coordinates.forEach(coord => {
      const pt = map.latLngToContainerPoint([coord.lat, coord.lng]);
      const color = coord.color ?? '#ff3b3b';
      const rgb = hexToRgb(color);

      // halo diffus
      const halo = ctx.createRadialGradient(pt.x, pt.y, 0, pt.x, pt.y, 36);
      halo.addColorStop(0,   `rgba(${rgb}, 0.55)`);
      halo.addColorStop(0.4, `rgba(${rgb}, 0.18)`);
      halo.addColorStop(1,   `rgba(${rgb}, 0)`);
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, 36, 0, Math.PI * 2);
      ctx.fillStyle = halo;
      ctx.fill();

      // point central — même couleur que le marker
      const core = ctx.createRadialGradient(pt.x, pt.y, 0, pt.x, pt.y, 6);
      core.addColorStop(0,   `rgba(${rgb}, 1)`);
      core.addColorStop(0.5, `rgba(${rgb}, 0.85)`);
      core.addColorStop(1,   `rgba(${rgb}, 0)`);
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, 6, 0, Math.PI * 2);
      ctx.fillStyle = core;
      ctx.fill();
    });
  }, [map, coordinates]);

  useEffect(() => {
    draw();
    map.on('moveend zoomend resize', draw);
    return () => { map.off('moveend zoomend resize', draw); };
  }, [map, draw]);

  return <canvas ref={canvasRef} className={styles.heatmapCanvas} />;
};

/* =========================
   SVG Marker Factory avec ombre intégrée
========================= */
function useIconFactory() {
  const cache = useRef<Map<string, L.DivIcon>>(new Map());
  const getIcon = useCallback((color: string = '#ff3b3b') => {
    if (cache.current.has(color)) return cache.current.get(color)!;
    const svg = `
<svg viewBox="0 0 28 42" width="28" height="42" xmlns="http://www.w3.org/2000/svg" style="overflow: visible;">
  <path d="M14 0C6 0 0 6 0 14c0 10 14 28 14 28s14-18 14-28C28 6 22 0 14 0z" fill="${color}" />
  <circle cx="14" cy="14" r="5" fill="white"/>
</svg>
`;
    const icon = new L.DivIcon({
      html: svg,
      className: styles.marker,
      iconSize: [28, 42],
      iconAnchor: [14, 42],
      popupAnchor: [0, -36],
    });
    cache.current.set(color, icon);
    return icon;
  }, []);
  return getIcon;
}

/* =========================
   Composant principal
========================= */
export const OpenStreetMap = ({
  coordinates,
  width = '100%',
  height = '100%',
  initialZoom = 4,
  renderMode = 'street',
  tileUrl,
}: OpenStreetMapProps) => {
  const [center, setCenter] = useState<Coordinate>({
    lat: 47.08,
    lng: 2.33,
    info1: '',
    info2: '',
  });
  const [zoom, setZoom] = useState<number>(initialZoom);
  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const getIcon = useIconFactory();

  useEffect(() => setZoom(initialZoom), [initialZoom]);

  useEffect(() => {
    if (coordinates.length > 0) {
      const avgLat = coordinates.reduce((sum, c) => sum + c.lat, 0) / coordinates.length;
      const avgLng = coordinates.reduce((sum, c) => sum + c.lng, 0) / coordinates.length;
      setCenter({ lat: avgLat, lng: avgLng, info1: '', info2: '' });
    }
  }, [coordinates]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => {
      mapRef.current?.invalidateSize();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} style={{ position: 'relative', width, height }}>
      <MapContainer
        center={center}
        zoom={zoom}
        zoomControl={false}
        ref={mapRef}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', borderRadius: '10px' }}
      >
        <TileLayer
          url={
            tileUrl ?? (
              renderMode === 'dark'
                ? TILE_URLS.darkLabels
                : TILE_URLS.street
            )
          }
          attribution="© OpenStreetMap contributors © CARTO"
        />
        <CustomZoomControl />

        {renderMode === 'dark' && (
          <HeatmapCanvas coordinates={coordinates} />
        )}

        {renderMode === 'street' && coordinates.map((coord, idx) => (
          <Marker key={idx} position={[coord.lat, coord.lng]} icon={getIcon(coord.color)}>
            <Popup>
              {coord.info1}{coord.info2 ? `, ${coord.info2}` : ''}
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default OpenStreetMap;